import React from 'react'
import PropTypes from 'prop-types'
import { IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Typography from 'components/mui/Typography'
import AppCardComponent from 'components/mui/AppCardComponent'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import AppTypographyComponent from 'components/mui/AppTypographyComponent'
import Stack from 'components/mui/Stack'
import Box from 'components/mui/Box'
import BottomAction from 'components/common/BottomAction'

/**
 * PAN Card Form Component - UI-only component for PAN card form
 * @component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} PanCardFormComponent
 */
const PanCardFormComponent = ({
  // Header props
  title,
  subtitle,
  skipText,
  showSkip = false,
  
  // Form section props
  sectionTitle,
  sectionDescription,
  
  // Button props
  buttonText,
  buttonVariant = 'contained',
  buttonLoading = false,
  buttonDisabled = false,
  
  // Note props
  noteText,
  showNote = false,
  
  // Event handlers
  onSubmit,
  onSkip,
  onBack,
  isMobile = false,
  // Styling props
  containerStyles = {},
  headerStyles = {},
  formCardStyles = {},
  noteStyles = {},
  buttonStyles = {},
  
  // Children (form inputs)
  children
}) => {
  // Default styles
  const defaultContainerStyles = {
    width: '100%',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...containerStyles
  }

  
  const defaultFormCardStyles = {
    width: '100%',
    backgroundColor: '#1C2033',
    borderRadius: '8px',
    padding: '16px',
    '& .MuiCardContent-root': {
      padding: '0 !important' // Remove CardContent padding
    },
    ...formCardStyles
  }
  
  const defaultNoteStyles = {
    width: { xs: '100%', md: 'fit-content' },
    maxWidth: { xs: '400px', md: 'none' },
    backgroundColor: '#173f53',
    borderRadius: '8px',
    padding: '8px 16px',
    marginBottom: '32px',
    ...noteStyles
  }
  
  const defaultButtonStyles = {
    // backgroundColor: '#707070',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 600,
    padding: '8px 22px',
    borderRadius: '8px',
    textTransform: 'none',
    ...buttonStyles
  }

  // mobile button styles
  const defaultMobileFormCardStyles = {
    width: '100%',
    backgroundColor: 'transparent',
    // ...formCardStyles
  }
  
  // Same back arrow as the Bank Account Linking screen (AppCardComponent header).
  const backButton = typeof onBack === 'function' ? (
    <IconButton
      onClick={() => onBack?.()}
      aria-label="back"
      sx={{
        color: 'white',
        padding: 0,
        marginRight: 1,
        '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
      }}
    >
      <ArrowBackIcon sx={{ fontSize: '1.2rem' }} />
    </IconButton>
  ) : null

  // Return/rendering
  return (
    <Box sx={defaultContainerStyles} >
      {/* Header Section */}
    
      {(title || subtitle || showSkip) && isMobile ? (null) : (
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', marginBottom: '24px' }}>
        <Box>
          {title && (
            <Typography
              component="div"
              sx={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', marginBottom: '8px', display: 'flex', alignItems: 'center' }}
            >
              {backButton}
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography sx={{ fontSize: '16px',fontWeight: 400, color: '#ffffff', marginBottom: '16px' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
          {showSkip && skipText && (
            <Typography 
              sx={{ fontSize: '16px', color: '#0078d4', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={onSkip}
            >
              {skipText}
            </Typography>
          )}
        </Stack>
      )}

      {/* Form Card */}
      <AppCardComponent
        sx={isMobile ? defaultMobileFormCardStyles : defaultFormCardStyles}
        elevation={0}
      >
        {/* Section Title */}
        {isMobile ? ( 
          <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', marginBottom: '10px' }}>
          <AppTypographyComponent
            variant="h4"
            sx={{ fontSize: '16px', fontWeight: 500, color: '#ffffff', display: 'flex', alignItems: 'center' }}
          >
            {backButton}
            Pan Card KYC
          </AppTypographyComponent>
          <Typography sx={{ fontSize: '14px', color: '#0078d4', textDecoration: 'underline', cursor: 'pointer' }} onClick={onSkip}>
            {skipText}
          </Typography>
          </Stack>
        ) : (sectionTitle && (
          <AppTypographyComponent
            variant="h4"
            sx={{ fontSize: '20px', fontWeight: 600, color: '#ffffff', marginBottom: '8px' }}
          >
            {sectionTitle}
          </AppTypographyComponent>
        ))}
        
        {sectionDescription && (
          <AppTypographyComponent
            variant="body2"
            sx={isMobile ? { fontSize: '12px',fontWeight: 400, color: '#b0b0b0', marginBottom: '32px' } : { fontSize: '14px',fontWeight: 400, color: '#b0b0b0', marginBottom: '32px' }}
          >
            {sectionDescription}
          </AppTypographyComponent>
        )}

        {/* Form */}
        <form onSubmit={onSubmit}>
          {/* Form Inputs as Children */}
          
          {children}

          {/* Note Box */}
          {showNote && noteText && (
            <Box sx={defaultNoteStyles}>
              <AppTypographyComponent
                variant="body2"
                sx={{ 
                  fontSize: '12px', 
                  color: '#ffffff', 
                  lineHeight: '122%',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {noteText}
              </AppTypographyComponent>
              <AppTypographyComponent
                variant="body2"
                sx={{ 
                  fontSize: '12px', 
                  color: '#ffffff', 
                  lineHeight: '122%',
                  display: { xs: 'block', sm: 'none' }
                }}
              >
                Note: Security code valid for 10 minutes only
              </AppTypographyComponent>
            </Box>
          )}

          {/* Submit Button */}

          {/* Submit Button */}
          {buttonText && !isMobile && (
            <AppButtonComponent
              type="submit"
              variant={buttonVariant}
              loading={buttonLoading}
              disabled={buttonDisabled}
              sx={defaultButtonStyles}
            >
              {buttonText}
            </AppButtonComponent>
          )}

          {/* Mobile Bottom Action Button */}
          {buttonText && isMobile && (
            <BottomAction>
               <AppButtonComponent
                type="submit"
                variant={buttonVariant}
                loading={buttonLoading}
                disabled={buttonDisabled}
                sx={{ ...defaultButtonStyles, width: '100%', margin: 0 }}
              >
                {buttonText}
              </AppButtonComponent>
            </BottomAction>
          )}


        </form>
      </AppCardComponent>
    </Box>
  )
}

PanCardFormComponent.propTypes = {
  // Header props
  title: PropTypes.string,
  subtitle: PropTypes.string,
  skipText: PropTypes.string,
  showSkip: PropTypes.bool,
  
  // Form section props
  sectionTitle: PropTypes.string,
  sectionDescription: PropTypes.string,
  
  // Button props
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  buttonLoading: PropTypes.bool,
  buttonDisabled: PropTypes.bool,
  
  // Note props
  noteText: PropTypes.string,
  showNote: PropTypes.bool,
  
  // Event handlers
  onSubmit: PropTypes.func,
  onSkip: PropTypes.func,
  onBack: PropTypes.func,
  
  // Styling props
  containerStyles: PropTypes.object,
  headerStyles: PropTypes.object,
  formCardStyles: PropTypes.object,
  noteStyles: PropTypes.object,
  buttonStyles: PropTypes.object,
  
  // Children (form inputs)
  children: PropTypes.node
}

PanCardFormComponent.defaultProps = {
  // Header props
  title: null,
  subtitle: null,
  skipText: null,
  showSkip: false,
  
  // Form section props
  sectionTitle: null,
  sectionDescription: null,
  
  // Button props
  buttonText: null,
  buttonVariant: 'contained',
  buttonLoading: false,
  buttonDisabled: false,
  
  // Note props
  noteText: null,
  showNote: false,
  
  // Event handlers
  onSubmit: () => {},
  onSkip: () => {},
  
  // Styling props
  containerStyles: {},
  headerStyles: {},
  formCardStyles: {},
  noteStyles: {},
  buttonStyles: {},
  
  // Children
  children: null
}

export default PanCardFormComponent
